import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSDataTypes = () => {
    const dynamicTypingCode = `let data = 42;              // Initially a Number
data = 'Hello';             // Reassigned to a String
data = true;                // Reassigned to a Boolean
data = { name: 'Alice' };   // Reassigned to an Object

// JavaScript engines handle this dynamic transition at runtime.`;

    const numberCode = `// Integers and Floating-point
let age = 25;
let pi = 3.14159;

// Special numeric values
let infinity = Infinity;
let notANumber = NaN;       // Result of invalid math (e.g., 'abc' / 2)

// Precise NaN checking
console.log(Number.isNaN(NaN));    // true
console.log(Number.isNaN('hello')); // false (strict and accurate)`;

    const floatingPointCode = `// The IEEE 754 Problem
console.log(0.1 + 0.2);          // 0.30000000000000004
console.log(0.1 + 0.2 === 0.3);  // false

// Engineering Solution: Use epsilon or rounding
const isCloseEnough = Math.abs((0.1 + 0.2) - 0.3) < Number.EPSILON;
console.log(isCloseEnough);      // true`;

    const stringCode = `// Template Literals (ES6+)
const user = 'Alice';
const greeting = \`Hello, \${user}!
The date is \${new Date().toLocaleDateString()}.\`;

// String methods (immutable)
const tech = 'JavaScript';
console.log(tech.slice(0, 4));      // "Java"
console.log(tech.toUpperCase());    // "JAVASCRIPT"`;

    const nullUndefinedCode = `let a;           // undefined (automatic: "I don't know what this is")
let b = null;    // null (manual: "I explicitly say this is empty")

console.log(typeof a);    // "undefined"
console.log(typeof b);    // "object" (Historical bug alert!)
console.log(a == b);      // true (Loose equality)
console.log(a === b);     // false (Strict equality)`;

    const bigIntCode = `// For numbers larger than 2^53 - 1
const huge = 9007199254740991n; 
const anotherHuge = BigInt("9007199254740991000");

const sum = huge + 10n; // Must use 'n' suffix
// const invalid = huge + 10; //  Error: Cannot mix types`;

    const symbolCode = `// Unique identifiers
const ID_KEY = Symbol('id');
const user = {
    name: 'Alice',
    [ID_KEY]: 12345
};

console.log(user[ID_KEY]); // 12345
// Symbols are invisible to Object.keys() and for...in loops`;

    const objectCode = `// Reference Types
const profile = {
    username: 'dev_hero',
    skills: ['JS', 'React']
};

const colors = ['red', 'green']; // Arrays are objects
console.log(typeof colors);      // "object"`;

    const passByValueCode = `// Primitives (Stack Memory)
let x = 10;
let y = x; // Copying the actual value
y = 20;
console.log(x); // 10 (Remains unchanged)`;

    const passByRefCode = `// Reference Types (Heap Memory)
let obj1 = { val: 10 };
let obj2 = obj1; // Copying the reference (memory address)
obj2.val = 20;

console.log(obj1.val); // 20 (Changed via reference!)`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">JavaScript Fundamentals</div>
                <h1>Data Types & Type System</h1>
                <p className="hero-lead">
                    Master JavaScript's dynamic type system. Understand the distinction between Primitive
                    and Reference types, memory allocation, and the technical edge cases of high-performance JS.
                </p>
            </div>

            <div className="content-section">
                <h2>The Type System Philosophy</h2>
                <p>
                    JavaScript is <strong>dynamically typed</strong> and <strong>weakly typed</strong>.
                    Unlike C++ or Java, variables are not bound to a specific type; instead, types are
                    associated with the <em>values</em> they hold. This provides immense flexibility but
                    requires a deep understanding of how the engine manages these transitions.
                </p>
                <CodeBlock language="javascript" code={dynamicTypingCode} />
            </div>

            <div className="content-section">
                <h2>Primitive Types (Stored by Value)</h2>
                <p>
                    Primitives are the simplest data types. They are <strong>immutable</strong> (the value itself
                    cannot change) and are stored directly in the "Stack" memory for fast access.
                </p>

                <div className="types-grid">
                    <div className="type-card">
                        <h3>1. Number</h3>
                        <p>Unlike other languages with <code>int</code>, <code>float</code>, and <code>double</code>,
                            JavaScript has only one type for numbers: 64-bit binary floating-point (IEEE 754).</p>
                        <CodeBlock language="javascript" code={numberCode} />

                        <Warning><strong> Floating Point Precision:</strong>
                            <p>Because JS uses binary math to represent decimal fractions, some calculations
                                (like 0.1 + 0.2) yield unexpected results.</p>
                            <CodeBlock language="javascript" code={floatingPointCode} /></Warning>
                    </div>

                    <div className="type-card">
                        <h3>2. String</h3>
                        <p>Strings are sequences of characters. In modern JS, <strong>Template Literals</strong>
                            (backticks) are the preferred way to handle dynamic text and multi-line strings.</p>
                        <CodeBlock language="javascript" code={stringCode} />
                    </div>

                    <div className="type-card">
                        <h3>3. Boolean</h3>
                        <p>Logical values: <code>true</code> or <code>false</code>. Crucial for control flow
                            logic and conditional rendering in frameworks like React.</p>
                    </div>

                    <div className="type-card">
                        <h3>4. Undefined & Null</h3>
                        <p>These represent "nothingness" but carry different semantic meanings in a codebase.</p>
                        <CodeBlock language="javascript" code={nullUndefinedCode} />
                    </div>

                    <div className="type-card">
                        <h3>5. BigInt (ES2020)</h3>
                        <p>Used for integers larger than <code>Number.MAX_SAFE_INTEGER</code>. This is critical
                            for financial applications or high-precision cryptography.</p>
                        <CodeBlock language="javascript" code={bigIntCode} />
                    </div>

                    <div className="type-card">
                        <h3>6. Symbol (ES6)</h3>
                        <p>Symbols generate unique identifiers. They are primarily used to add hidden
                            metadata to objects without clashing with existing keys.</p>
                        <CodeBlock language="javascript" code={symbolCode} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Reference Types (Objects)</h2>
                <p>
                    Everything else in JavaScript is an <strong>Object</strong>. Unlike primitives,
                    objects are complex structures stored in the "Heap" memory. When you assign an object
                    to a variable, you are storing a <strong>pointer</strong> to that memory location.
                </p>
                <CodeBlock language="javascript" code={objectCode} />
                <ProTip><strong> Fact:</strong> Even <code>Arrays</code> and <code>Functions</code> are
                    technically objects under the hood! You can verify this by checking
                    <code>typeof someFunction</code> which returns "function" (a callable object).</ProTip>
            </div>

            <div className="content-section">
                <h2>Memory Management: Stack vs Heap</h2>
                <p>
                    Understanding the memory model is what separates junior developers from senior engineers.
                    It explains how data is shared across your application.
                </p>

                <h3>Pass by Value (Stack)</h3>
                <p>Simple primitives are copied entirely. Changing the copy does not affect the original.</p>
                <CodeBlock language="javascript" code={passByValueCode} />

                <h3>Pass by Reference (Heap)</h3>
                <p>Objects share a memory address. Modifying the object through one variable will reflect
                    across all variables pointing to that same object.</p>
                <CodeBlock language="javascript" code={passByRefCode} />
            </div>

            <div className="content-section">
                <h2>Professional Type Checking</h2>
                <p>
                    Because of JavaScript's dynamic nature, verifying types is essential for bug prevention.
                    The <code>typeof</code> operator is helpful but has limitations (like for arrays and null).
                </p>
                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th>Scenario</th>
                            <th>Best Practice Approach</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Basic Primitives</td>
                            <td><code>typeof x === 'string' / 'number'</code></td>
                        </tr>
                        <tr>
                            <td>Arrays</td>
                            <td><code>Array.isArray(x)</code></td>
                        </tr>
                        <tr>
                            <td>Null</td>
                            <td><code>x === null</code></td>
                        </tr>
                        <tr>
                            <td>Instance of Class</td>
                            <td><code>x instanceof Date / Map</code></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <KeyFacts><ul>
                    <li>There are 7 Primitives + Objects.</li>
                    <li>Primitives are immutable and stored in the <strong>Stack</strong>.</li>
                    <li>Objects are mutable, stored in the <strong>Heap</strong>, and accessed by reference.</li>
                    <li>Use <code>BigInt</code> for large-scale mathematical operations.</li>
                    <li>Be cautious of <code>0.1 + 0.2</code> floating-point precision issues.</li>
                    <li>Use <code>Array.isArray()</code> instead of <code>typeof</code> for checking arrays.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDataTypes;


